import { NODE_TYPES, GENERAL_SHAPE_TYPES, isMarkupType, isMagneticNode } from '../constants/nodeTypes';
import { isDiagramKitId, type DiagramKitId } from './diagramKits';
import { NodeFactory } from '../services/node/NodeFactory';
import { buildSocketsFromType, calcNodeHeight, calcDecisionSize, rebuildSketchSockets } from './socketLayout';
import {
  Node,
  Edge,
  Layer,
  NodeMeta,
  NodeGroup,
  DiagramType,
  DiagramPage,
  PageSettings,
  SequenceParticipant,
  SequenceMessage,
  SequenceFragment,
  SequenceParticipantGroup,
  Graph,
  EdgeDashStyle,
  EdgeType,
  CatalogComponent,
  SocketDefinition,
} from '../types';
import { inferCatalogFromDocument } from './catalog';

const NODE_WIDTH = 180;
const DEFAULT_LAYER: Layer = { id: 'default', name: 'Default', color: '#6366f1', visible: true, dimmed: false };
const COORD_PRECISION = 1000;
const quantize = (value: number) => Math.round(value * COORD_PRECISION) / COORD_PRECISION;

type RefId = number;

interface SerializedSocket {
  id: string;
  type: 'input' | 'output';
  label?: string;
  pos: [number, number];
  /**
   * Which way the wire leaves. Written only when layout put the socket
   * somewhere the position alone would not explain - a port near a corner is
   * otherwise read as belonging to the wrong face.
   */
  tickSide?: SocketDefinition['tickSide'];
}

interface SerializedNode {
  id: string;
  type: string;
  name: string;
  pos: [number, number];
  size?: [number, number];
  sockets?: SerializedSocket[];
  extra_inputs?: { name: string }[];
  extra_outputs?: { name: string }[];
  layer?: string;
  parentGroupId?: string;
  childGraphId?: string;
  diamondRotation?: number;
  subPageId?: string;
  linkedPageId?: string;
  catalogId?: string;
  stackParentId?: string;
  stackSnap?: 'center' | 'x' | 'y';
  uml?: {
    attributes?: string[];
    methods?: string[];
    stereotype?: string;
  };
  markup?: {
    from?: [number, number];
    to?: [number, number];
    edgeType?: EdgeType;
    strokeWidth?: number;
    dashStyle?: EdgeDashStyle;
    color?: string;
  };
  meta?: {
    description?: string;
    owner?: string;
    status?: string;
    links?: { label: string; url: string }[];
  };
}

interface SerializedLink {
  from: string;
  out: number | string;
  to: string;
  in: number | string;
  label?: string;
  animated?: boolean;
  waypoints?: [number, number][];
  /**
   * The route automatic layout computed, kept apart from `waypoints` so a
   * user-drawn bend is never mistaken for a derived one. `from`/`to` are the
   * socket positions it was computed against; the route is discarded on load
   * if either has moved, so a stale polyline can never outlive its endpoints.
   */
  autoRoute?: {
    from: [number, number];
    to: [number, number];
    points: [number, number][];
  };
  routing?: string;
  curve?: number;
  strokeWidth?: number;
  dashStyle?: EdgeDashStyle;
  edgeType?: EdgeType;
  color?: string;
}

interface SerializedPage {
  id: string;
  name: string;
  diagramType?: DiagramType;
  kit?: DiagramKitId;
  nodes: SerializedNode[];
  links: SerializedLink[];
  viewport: { x: number; y: number; scale: number };
  layers?: { id: string; name: string; color: string; visible: boolean; dimmed: boolean }[];
  groups?: NodeGroup[];
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  seqLifelineLength?: number;
  /** Per-page overrides. Absent on a page that has not chosen any. */
  settings?: PageSettings;
}

interface SerializedGraph {
  id: string;
  nodes: SerializedNode[];
  links: SerializedLink[];
  viewport: { x: number; y: number; scale: number };
}

export interface DiagramData {
  version: string;
  name: string;
  graphs?: Record<string, SerializedGraph>;
  activeGraphId?: string;
  pages?: SerializedPage[];
  activePage?: string;
  nodes?: SerializedNode[];
  links?: SerializedLink[];
  viewport?: { x: number; y: number; scale: number };
  layers?: { id: string; name: string; color: string; visible: boolean; dimmed: boolean }[];
  groups?: NodeGroup[];
  diagramType?: DiagramType;
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  seqLifelineLength?: number;
  catalog?: CatalogComponent[];
}

type NodeTypeCode = 'f' | 's';
type MessageTypeCode = 's' | 'a' | 'r';
type FragmentTypeCode = 'l' | 'a' | 'o' | 'p';

interface V4Layer {
  i: RefId;
  n: string;
  c: string;
  v?: 0 | 1;
  d?: 1;
}

interface V4NodeMeta {
  d?: string;
  o?: string;
  s?: string;
  l?: { label: string; url: string }[];
}

type V4Socket = [string, 0 | 1, number, number, string?, TickCode?];

/** Tick side, one letter: left, right, up, down. */
type TickCode = 'l' | 'r' | 'u' | 'd';

const TICK_TO_CODE: Record<NonNullable<SocketDefinition['tickSide']>, TickCode> = {
  left: 'l', right: 'r', up: 'u', down: 'd',
};
const CODE_TO_TICK: Record<TickCode, NonNullable<SocketDefinition['tickSide']>> = {
  l: 'left', r: 'right', u: 'up', d: 'down',
};

interface V4Node {
  i: RefId;
  t: string;
  n: string;
  p: [number, number];
  z?: [number, number];
  k?: V4Socket[];
  ei?: string[];
  eo?: string[];
  ly?: RefId;
  pg?: RefId;
  cg?: RefId;
  dr?: number;
  sp?: RefId;
  lp?: RefId;
  ci?: RefId;
  sk?: RefId;
  ss?: 'c' | 'x' | 'y';
  mk?: {
    f?: [number, number];
    t?: [number, number];
    et?: EdgeType;
    sw?: number;
    ds?: EdgeDashStyle;
    c?: string;
  };
  m?: V4NodeMeta;
  u?: { a?: string[]; m?: string[]; s?: string };
}

interface V4Link {
  f: RefId;
  o: number | string;
  t: RefId;
  i: number | string;
  lb?: string;
  an?: 1;
  wp?: [number, number][];
  /** Automatic route as `[from, to, ...corners]`. See `SerializedLink`. */
  ar?: [number, number][];
  rt?: 'b' | 'o';
  cv?: number;
  sw?: number;
  ds?: EdgeDashStyle;
  et?: EdgeType;
  c?: string;
}

interface V4NodeGroup {
  i: RefId;
  l: string;
  c: string;
  n: RefId[];
  g: RefId[];
  p?: RefId;
  cl?: 1;
  x: [number, number];
  z: [number, number];
  f?: 1;
  gi?: RefId;
  go?: RefId;
}

interface V4Participant {
  i: RefId;
  t: string;
  n: string;
  c: string;
  o: number;
  x: number;
  ly?: RefId;
  d?: string;
  l?: { label: string; url: string }[];
  lp?: RefId;
  ci?: RefId;
}

interface V4Message {
  i: RefId;
  s: RefId;
  t: RefId;
  y: number;
  l: string;
  k: MessageTypeCode;
  c?: string;
  sw?: number;
  ds?: EdgeDashStyle;
  ox?: number;
  d?: string;
  tt?: string;
  py?: string;
  ct?: string;
  ln?: { label: string; url: string }[];
  f?: RefId;
}

interface V4Fragment {
  i: RefId;
  t: FragmentTypeCode;
  l: string;
  sp?: [RefId, RefId];
  g: [number, number, number?];
  pf?: RefId;
}

interface V4ParticipantGroup {
  i: RefId;
  l: string;
  c: string;
  ch: RefId[];
  cl?: 1;
}

interface V4Sequence {
  p?: V4Participant[];
  m?: V4Message[];
  f?: V4Fragment[];
  g?: V4ParticipantGroup[];
  ll?: number;
}

interface V4Page {
  i: RefId;
  n: string;
  dt?: NodeTypeCode;
  k?: DiagramKitId;
  nd: V4Node[];
  lk: V4Link[];
  vp: [number, number, number];
  ly?: V4Layer[];
  gr?: V4NodeGroup[];
  sq?: V4Sequence;
  /**
   * The page's overrides, written out under their own names. There is at most
   * one of these per page, so the codes the rest of the format uses would buy
   * nothing but a second table to keep in step.
   */
  st?: PageSettings;
}

interface V4Catalog {
  i: RefId;
  n: string;
  t: string;
  lv?: string;
  d?: string;
  o?: string;
  s?: string;
  l?: { label: string; url: string }[];
  th?: string;
  c?: string;
}

interface V4Graph {
  i: RefId;
  nd: V4Node[];
  lk: V4Link[];
  vp: [number, number, number];
}

interface DiagramDataV4 {
  version: '4.0';
  name: string;
  ids: string[];
  dt?: NodeTypeCode;
  ap?: RefId;
  ag?: RefId;
  p?: V4Page[];
  g?: V4Graph[];
  nd?: V4Node[];
  lk?: V4Link[];
  vp?: [number, number, number];
  ly?: V4Layer[];
  gr?: V4NodeGroup[];
  sq?: V4Sequence;
  ca?: V4Catalog[];
}

const BOUNDARY_TYPES = new Set(['group_input', 'group_output']);
const BOUNDARY_TYPES_DESER = new Set(['group_input', 'group_output']);

function encodeDiagramType(value?: DiagramType): NodeTypeCode | undefined {
  if (!value) return undefined;
  return value === 'sequence' ? 's' : 'f';
}

function decodeDiagramType(value?: NodeTypeCode): DiagramType | undefined {
  if (!value) return undefined;
  return value === 's' ? 'sequence' : 'flow';
}

function encodeMessageType(value: SequenceMessage['type']): MessageTypeCode {
  if (value === 'async') return 'a';
  if (value === 'return') return 'r';
  return 's';
}

function decodeMessageType(value: MessageTypeCode): SequenceMessage['type'] {
  if (value === 'a') return 'async';
  if (value === 'r') return 'return';
  return 'sync';
}

function encodeFragmentType(value: SequenceFragment['type']): FragmentTypeCode {
  if (value === 'alt') return 'a';
  if (value === 'opt') return 'o';
  if (value === 'par') return 'p';
  return 'l';
}

function decodeFragmentType(value: FragmentTypeCode): SequenceFragment['type'] {
  if (value === 'a') return 'alt';
  if (value === 'o') return 'opt';
  if (value === 'p') return 'par';
  return 'loop';
}

function encodeNodeMeta(meta?: SerializedNode['meta']): V4NodeMeta | undefined {
  if (!meta) return undefined;
  const out: V4NodeMeta = {};
  if (meta.description) out.d = meta.description;
  if (meta.owner) out.o = meta.owner;
  if (meta.status) out.s = meta.status;
  if (meta.links && meta.links.length > 0) out.l = meta.links;
  return Object.keys(out).length > 0 ? out : undefined;
}

function decodeNodeMeta(meta?: V4NodeMeta): SerializedNode['meta'] | undefined {
  if (!meta) return undefined;
  const out: SerializedNode['meta'] = {};
  if (meta.d) out.description = meta.d;
  if (meta.o) out.owner = meta.o;
  if (meta.s) out.status = meta.s;
  if (meta.l && meta.l.length > 0) out.links = meta.l;
  return Object.keys(out).length > 0 ? out : undefined;
}

function createIdCodec(existing?: string[]) {
  const ids: string[] = existing ? [...existing] : [];
  const map = new Map<string, number>();
  ids.forEach((id, idx) => map.set(id, idx));

  const ref = (id?: string): number | undefined => {
    if (!id) return undefined;
    const hit = map.get(id);
    if (hit !== undefined) return hit;
    const next = ids.length;
    ids.push(id);
    map.set(id, next);
    return next;
  };

  const deref = (value?: number): string | undefined => {
    if (value === undefined || value < 0 || value >= ids.length) return undefined;
    return ids[value];
  };

  return { ids, ref, deref };
}

function encodeCatalog(
  catalog: CatalogComponent[] | undefined,
  codec: ReturnType<typeof createIdCodec>,
): V4Catalog[] | undefined {
  if (!catalog || catalog.length === 0) return undefined;
  return catalog.map((entry) => ({
    i: codec.ref(entry.id)!,
    n: entry.name,
    t: entry.type,
    lv: entry.c4Level,
    d: entry.description,
    o: entry.owner,
    s: entry.status,
    l: entry.links?.length ? entry.links : undefined,
    th: entry.tech,
    c: entry.color,
  }));
}

function decodeCatalog(
  catalog: V4Catalog[] | undefined,
  codec: ReturnType<typeof createIdCodec>,
): CatalogComponent[] | undefined {
  if (!catalog || catalog.length === 0) return undefined;
  return catalog.map((entry) => ({
    id: codec.deref(entry.i) ?? `cat-${entry.i}`,
    name: entry.n,
    type: entry.t,
    c4Level: entry.lv as CatalogComponent['c4Level'],
    description: entry.d,
    owner: entry.o,
    status: entry.s as CatalogComponent['status'],
    links: entry.l,
    tech: entry.th,
    color: entry.c,
  }));
}

function encodeLayers(layers: Layer[] | undefined, codec: ReturnType<typeof createIdCodec>): V4Layer[] | undefined {
  if (!layers || layers.length === 0) return undefined;
  return layers.map((layer) => ({
    i: codec.ref(layer.id)!,
    n: layer.name,
    c: layer.color,
    v: layer.visible ? undefined : 0,
    d: layer.dimmed ? 1 : undefined,
  }));
}

function decodeLayers(layers: V4Layer[] | undefined, codec: ReturnType<typeof createIdCodec>): Layer[] | undefined {
  if (!layers || layers.length === 0) return undefined;
  return layers.map((layer) => ({
    id: codec.deref(layer.i) ?? `layer-${layer.i}`,
    name: layer.n,
    color: layer.c,
    visible: layer.v !== 0,
    dimmed: !!layer.d,
  }));
}

function encodeNodeGroups(groups: NodeGroup[] | undefined, codec: ReturnType<typeof createIdCodec>): V4NodeGroup[] | undefined {
  if (!groups || groups.length === 0) return undefined;
  return groups.map((group) => ({
    i: codec.ref(group.id)!,
    l: group.label,
    c: group.color,
    n: group.childNodeIds.map((id) => codec.ref(id)!),
    g: (group.childGroupIds ?? []).map((id) => codec.ref(id)!),
    p: codec.ref(group.parentGroupId),
    cl: group.collapsed ? 1 : undefined,
    x: [quantize(group.position.x), quantize(group.position.y)],
    z: [quantize(group.size.width), quantize(group.size.height)],
    f: group.isFrame ? 1 : undefined,
    gi: codec.ref(group.groupInputNodeId),
    go: codec.ref(group.groupOutputNodeId),
  }));
}

function decodeNodeGroups(groups: V4NodeGroup[] | undefined, codec: ReturnType<typeof createIdCodec>): NodeGroup[] | undefined {
  if (!groups || groups.length === 0) return undefined;
  return groups.map((group) => ({
    id: codec.deref(group.i) ?? `group-${group.i}`,
    label: group.l,
    color: group.c,
    childNodeIds: group.n.map((ref) => codec.deref(ref)).filter((id): id is string => !!id),
    childGroupIds: (group.g ?? []).map((ref) => codec.deref(ref)).filter((id): id is string => !!id),
    parentGroupId: codec.deref(group.p),
    collapsed: !!group.cl,
    position: { x: group.x[0], y: group.x[1] },
    size: { width: group.z[0], height: group.z[1] },
    isFrame: !!group.f,
    groupInputNodeId: codec.deref(group.gi),
    groupOutputNodeId: codec.deref(group.go),
  }));
}

function encodeSerializedNodes(nodes: SerializedNode[], codec: ReturnType<typeof createIdCodec>): V4Node[] {
  return nodes.map((node) => ({
    i: codec.ref(node.id)!,
    t: node.type,
    n: node.name,
    p: [quantize(node.pos[0]), quantize(node.pos[1])],
    z: node.size ? [quantize(node.size[0]), quantize(node.size[1])] : undefined,
    k: node.sockets?.map((socket) => [
      socket.id,
      socket.type === 'output' ? 1 : 0,
      quantize(socket.pos[0]),
      quantize(socket.pos[1]),
      socket.label,
      socket.tickSide ? TICK_TO_CODE[socket.tickSide] : undefined,
    ] as V4Socket),
    ei: node.extra_inputs?.map((input) => input.name),
    eo: node.extra_outputs?.map((output) => output.name),
    ly: codec.ref(node.layer),
    pg: codec.ref(node.parentGroupId),
    cg: codec.ref(node.childGraphId),
    dr: node.diamondRotation,
    sp: codec.ref(node.subPageId),
    lp: codec.ref(node.linkedPageId),
    ci: codec.ref(node.catalogId),
    sk: codec.ref(node.stackParentId),
    ss: node.stackSnap === 'center' ? 'c' : node.stackSnap === 'x' || node.stackSnap === 'y' ? node.stackSnap : undefined,
    mk: node.markup ? {
      f: node.markup.from,
      t: node.markup.to,
      et: node.markup.edgeType,
      sw: node.markup.strokeWidth,
      ds: node.markup.dashStyle,
      c: node.markup.color,
    } : undefined,
    m: encodeNodeMeta(node.meta),
    u: node.uml && (node.uml.attributes?.length || node.uml.methods?.length || node.uml.stereotype)
      ? { a: node.uml.attributes, m: node.uml.methods, s: node.uml.stereotype }
      : undefined,
  }));
}

function decodeSerializedNodes(nodes: V4Node[] | undefined, codec: ReturnType<typeof createIdCodec>): SerializedNode[] {
  if (!nodes || nodes.length === 0) return [];
  return nodes.map((node) => ({
    id: codec.deref(node.i) ?? `node-${node.i}`,
    type: node.t,
    name: node.n,
    pos: [node.p[0], node.p[1]],
    size: node.z ? [node.z[0], node.z[1]] : undefined,
    sockets: node.k?.map(([id, typeCode, x, y, label, tick]) => ({
      id,
      type: typeCode === 1 ? 'output' : 'input',
      label,
      pos: [x, y],
      tickSide: tick ? CODE_TO_TICK[tick] : undefined,
    })),
    extra_inputs: node.ei?.map((name) => ({ name })),
    extra_outputs: node.eo?.map((name) => ({ name })),
    layer: codec.deref(node.ly),
    parentGroupId: codec.deref(node.pg),
    childGraphId: codec.deref(node.cg),
    diamondRotation: node.dr,
    subPageId: codec.deref(node.sp),
    linkedPageId: codec.deref(node.lp),
    catalogId: codec.deref(node.ci),
    stackParentId: codec.deref(node.sk),
    stackSnap: node.ss === 'c' ? 'center' : node.ss === 'x' || node.ss === 'y' ? node.ss : undefined,
    markup: node.mk ? {
      from: node.mk.f,
      to: node.mk.t,
      edgeType: node.mk.et,
      strokeWidth: node.mk.sw,
      dashStyle: node.mk.ds,
      color: node.mk.c,
    } : undefined,
    meta: decodeNodeMeta(node.m),
    uml: node.u ? { attributes: node.u.a, methods: node.u.m, stereotype: node.u.s } : undefined,
  }));
}

function encodeSerializedLinks(links: SerializedLink[], codec: ReturnType<typeof createIdCodec>): V4Link[] {
  return links.map((link) => ({
    f: codec.ref(link.from)!,
    o: link.out,
    t: codec.ref(link.to)!,
    i: link.in,
    lb: link.label,
    an: link.animated ? 1 : undefined,
    wp: link.waypoints?.map(([x, y]) => [quantize(x), quantize(y)]),
    ar: link.autoRoute
      ? ([link.autoRoute.from, link.autoRoute.to, ...link.autoRoute.points]
        .map(([x, y]) => [quantize(x), quantize(y)]) as [number, number][])
      : undefined,
    rt: link.routing === 'orthogonal' ? 'o' : link.routing === 'bezier' ? 'b' : undefined,
    cv: link.curve,
    sw: link.strokeWidth,
    ds: link.dashStyle,
    et: link.edgeType,
    c: link.color,
  }));
}

function decodeSerializedLinks(links: V4Link[] | undefined, codec: ReturnType<typeof createIdCodec>): SerializedLink[] {
  if (!links || links.length === 0) return [];
  return links.map((link) => ({
    from: codec.deref(link.f) ?? `node-${link.f}`,
    out: link.o,
    to: codec.deref(link.t) ?? `node-${link.t}`,
    in: link.i,
    label: link.lb,
    animated: link.an ? true : undefined,
    waypoints: link.wp?.map(([x, y]) => [x, y]),
    autoRoute: link.ar && link.ar.length >= 2
      ? { from: link.ar[0], to: link.ar[1], points: link.ar.slice(2) }
      : undefined,
    routing: link.rt === 'o' ? 'orthogonal' : link.rt === 'b' ? 'bezier' : undefined,
    curve: link.cv,
    strokeWidth: link.sw,
    dashStyle: link.ds,
    edgeType: link.et,
    color: link.c,
  }));
}

function buildParticipantSpan(fragment: SequenceFragment, participants: SequenceParticipant[], codec: ReturnType<typeof createIdCodec>): [RefId, RefId] | undefined {
  if (!fragment.participantIds || fragment.participantIds.length === 0 || participants.length === 0) return undefined;
  const ordered = [...participants].sort((a, b) => a.order - b.order || a.x - b.x);
  const indexes = fragment.participantIds
    .map((id) => ordered.findIndex((participant) => participant.id === id))
    .filter((idx) => idx >= 0);
  if (indexes.length === 0) return undefined;
  const from = ordered[Math.min(...indexes)];
  const to = ordered[Math.max(...indexes)];
  return [codec.ref(from.id)!, codec.ref(to.id)!];
}

function expandParticipantSpan(span: [RefId, RefId] | undefined, participants: SequenceParticipant[], codec: ReturnType<typeof createIdCodec>): string[] {
  if (!span) return [];
  const fromId = codec.deref(span[0]);
  const toId = codec.deref(span[1]);
  if (!fromId || !toId) return [fromId, toId].filter((id): id is string => !!id);
  const ordered = [...participants].sort((a, b) => a.order - b.order || a.x - b.x);
  const start = ordered.findIndex((participant) => participant.id === fromId);
  const end = ordered.findIndex((participant) => participant.id === toId);
  if (start === -1 || end === -1) return [fromId, toId];
  const [left, right] = start <= end ? [start, end] : [end, start];
  return ordered.slice(left, right + 1).map((participant) => participant.id);
}

function encodeSequence(
  participants: SequenceParticipant[] | undefined,
  messages: SequenceMessage[] | undefined,
  fragments: SequenceFragment[] | undefined,
  participantGroups: SequenceParticipantGroup[] | undefined,
  seqLifelineLength: number | undefined,
  codec: ReturnType<typeof createIdCodec>,
): V4Sequence | undefined {
  const out: V4Sequence = {};

  if (participants && participants.length > 0) {
    out.p = participants.map((participant) => ({
      i: codec.ref(participant.id)!,
      t: participant.type,
      n: participant.label,
      c: participant.color,
      o: participant.order,
      x: quantize(participant.x),
      ly: codec.ref(participant.layerId),
      d: participant.description,
      l: participant.links?.length ? participant.links : undefined,
      lp: codec.ref(participant.linkedPageId),
      ci: codec.ref(participant.catalogId),
    }));
  }

  if (messages && messages.length > 0) {
    out.m = messages.map((message) => ({
      i: codec.ref(message.id)!,
      s: codec.ref(message.sourceId)!,
      t: codec.ref(message.targetId)!,
      y: quantize(message.y),
      l: message.label,
      k: encodeMessageType(message.type),
      c: message.color,
      sw: message.strokeWidth,
      ds: message.dashStyle,
      ox: message.labelOffsetX !== undefined ? quantize(message.labelOffsetX) : undefined,
      d: message.description,
      tt: message.tooltip,
      py: message.payload,
      ct: message.constraints,
      ln: message.links?.length ? message.links : undefined,
      f: codec.ref(message.fragmentId),
    }));
  }

  if (fragments && fragments.length > 0) {
    const participantList = participants ?? [];
    out.f = fragments.map((fragment) => ({
      i: codec.ref(fragment.id)!,
      t: encodeFragmentType(fragment.type),
      l: fragment.label,
      sp: buildParticipantSpan(fragment, participantList, codec),
      g: [
        quantize(fragment.startY),
        quantize(fragment.endY),
        fragment.altDividerY !== undefined ? quantize(fragment.altDividerY) : undefined,
      ],
      pf: codec.ref(fragment.parentFragmentId),
    }));
  }

  if (participantGroups && participantGroups.length > 0) {
    out.g = participantGroups.map((group) => ({
      i: codec.ref(group.id)!,
      l: group.label,
      c: group.color,
      ch: group.childParticipantIds.map((id) => codec.ref(id)!),
      cl: group.collapsed ? 1 : undefined,
    }));
  }

  if (seqLifelineLength !== undefined) out.ll = quantize(seqLifelineLength);
  return Object.keys(out).length > 0 ? out : undefined;
}

function decodeSequence(
  sequence: V4Sequence | undefined,
  codec: ReturnType<typeof createIdCodec>,
): {
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength?: number;
} {
  const participants: SequenceParticipant[] = (sequence?.p ?? []).map((participant) => ({
    id: codec.deref(participant.i) ?? `participant-${participant.i}`,
    type: participant.t,
    label: participant.n,
    color: participant.c,
    order: participant.o,
    x: participant.x,
    layerId: codec.deref(participant.ly),
    description: participant.d,
    links: participant.l,
    linkedPageId: codec.deref(participant.lp),
    catalogId: codec.deref(participant.ci),
  }));

  const messages: SequenceMessage[] = (sequence?.m ?? []).map((message) => ({
    id: codec.deref(message.i) ?? `message-${message.i}`,
    sourceId: codec.deref(message.s) ?? `participant-${message.s}`,
    targetId: codec.deref(message.t) ?? `participant-${message.t}`,
    y: message.y,
    label: message.l,
    type: decodeMessageType(message.k),
    color: message.c,
    strokeWidth: message.sw,
    dashStyle: message.ds,
    labelOffsetX: message.ox,
    description: message.d,
    tooltip: message.tt,
    payload: message.py,
    constraints: message.ct,
    links: message.ln,
    fragmentId: codec.deref(message.f),
  }));

  const fragments: SequenceFragment[] = (sequence?.f ?? []).map((fragment) => ({
    id: codec.deref(fragment.i) ?? `fragment-${fragment.i}`,
    type: decodeFragmentType(fragment.t),
    label: fragment.l,
    participantIds: expandParticipantSpan(fragment.sp, participants, codec),
    startY: fragment.g[0],
    endY: fragment.g[1],
    altDividerY: fragment.g[2],
    parentFragmentId: codec.deref(fragment.pf),
  }));

  const participantGroups: SequenceParticipantGroup[] = (sequence?.g ?? []).map((group) => ({
    id: codec.deref(group.i) ?? `pgroup-${group.i}`,
    label: group.l,
    color: group.c,
    childParticipantIds: group.ch.map((ref) => codec.deref(ref)).filter((id): id is string => !!id),
    collapsed: !!group.cl,
  }));

  return {
    participants,
    messages,
    fragments,
    participantGroups,
    seqLifelineLength: sequence?.ll,
  };
}

/**
 * Copy a page's overrides, dropping the ones that are not set. A page that has
 * chosen nothing writes no settings block at all, which is what keeps an
 * untouched page reading as "follow the app" rather than as a snapshot of
 * whatever the app default happened to be on the day it was saved.
 */
function encodePageSettings(settings: PageSettings | undefined): PageSettings | undefined {
  if (!settings) return undefined;
  const out: PageSettings = {};
  if (settings.routingMode) out.routingMode = settings.routingMode;
  if (typeof settings.textScale === 'number') out.textScale = settings.textScale;
  return Object.keys(out).length > 0 ? out : undefined;
}

function encodeV4(data: DiagramData): DiagramDataV4 {
  const codec = createIdCodec();

  const pages = data.pages?.map((page) => ({
    i: codec.ref(page.id)!,
    n: page.name,
    dt: encodeDiagramType(page.diagramType),
    k: page.kit && page.kit !== 'architecture' ? page.kit : undefined,
    nd: encodeSerializedNodes(page.nodes ?? [], codec),
    lk: encodeSerializedLinks(page.links ?? [], codec),
    vp: [quantize(page.viewport?.x ?? 0), quantize(page.viewport?.y ?? 0), quantize(page.viewport?.scale ?? 1)] as [number, number, number],
    ly: encodeLayers(page.layers as Layer[] | undefined, codec),
    gr: encodeNodeGroups(page.groups, codec),
    sq: encodeSequence(page.participants, page.messages, page.fragments, page.participantGroups, page.seqLifelineLength, codec),
    st: encodePageSettings(page.settings),
  }));

  const graphs = data.graphs
    ? Object.values(data.graphs).map((graph) => ({
        i: codec.ref(graph.id)!,
        nd: encodeSerializedNodes(graph.nodes ?? [], codec),
        lk: encodeSerializedLinks(graph.links ?? [], codec),
        vp: [quantize(graph.viewport?.x ?? 0), quantize(graph.viewport?.y ?? 0), quantize(graph.viewport?.scale ?? 1)] as [number, number, number],
      }))
    : undefined;

  const hasPages = !!(pages && pages.length > 0);
  const topSequence = hasPages
    ? undefined
    : encodeSequence(data.participants, data.messages, data.fragments, data.participantGroups, data.seqLifelineLength, codec);

  return {
    version: '4.0',
    name: data.name,
    ids: codec.ids,
    dt: encodeDiagramType(data.diagramType),
    ap: codec.ref(data.activePage),
    ag: codec.ref(data.activeGraphId),
    p: pages,
    g: graphs,
    nd: hasPages ? undefined : encodeSerializedNodes(data.nodes ?? [], codec),
    lk: hasPages ? undefined : encodeSerializedLinks(data.links ?? [], codec),
    vp: hasPages || !data.viewport
      ? undefined
      : [quantize(data.viewport.x), quantize(data.viewport.y), quantize(data.viewport.scale)],
    ly: hasPages ? undefined : encodeLayers(data.layers as Layer[] | undefined, codec),
    gr: hasPages ? undefined : encodeNodeGroups(data.groups, codec),
    sq: topSequence,
    ca: encodeCatalog(data.catalog, codec),
  };
}

function decodeV4(data: DiagramDataV4): DiagramData {
  const codec = createIdCodec(data.ids ?? []);

  const pages: SerializedPage[] | undefined = data.p?.map((page) => {
    const decodedSequence = decodeSequence(page.sq, codec);
    return {
      id: codec.deref(page.i) ?? `page-${page.i}`,
      name: page.n,
      diagramType: decodeDiagramType(page.dt),
      kit: isDiagramKitId(page.k) ? page.k : undefined,
      nodes: decodeSerializedNodes(page.nd, codec),
      links: decodeSerializedLinks(page.lk, codec),
      viewport: { x: page.vp[0], y: page.vp[1], scale: page.vp[2] },
      layers: decodeLayers(page.ly, codec),
      groups: decodeNodeGroups(page.gr, codec),
      participants: decodedSequence.participants,
      messages: decodedSequence.messages,
      fragments: decodedSequence.fragments,
      participantGroups: decodedSequence.participantGroups,
      seqLifelineLength: decodedSequence.seqLifelineLength,
      settings: encodePageSettings(page.st),
    };
  });

  const graphsArray: SerializedGraph[] | undefined = data.g?.map((graph) => ({
    id: codec.deref(graph.i) ?? `graph-${graph.i}`,
    nodes: decodeSerializedNodes(graph.nd, codec),
    links: decodeSerializedLinks(graph.lk, codec),
    viewport: { x: graph.vp[0], y: graph.vp[1], scale: graph.vp[2] },
  }));

  const graphRecord = graphsArray && graphsArray.length > 0
    ? Object.fromEntries(graphsArray.map((graph) => [graph.id, graph]))
    : undefined;

  const topSequence = decodeSequence(data.sq, codec);

  return {
    version: '4.0',
    name: data.name ?? 'My Architecture',
    graphs: graphRecord,
    activeGraphId: codec.deref(data.ag),
    pages,
    activePage: codec.deref(data.ap),
    nodes: decodeSerializedNodes(data.nd, codec),
    links: decodeSerializedLinks(data.lk, codec),
    viewport: data.vp ? { x: data.vp[0], y: data.vp[1], scale: data.vp[2] } : undefined,
    layers: decodeLayers(data.ly, codec),
    groups: decodeNodeGroups(data.gr, codec),
    diagramType: decodeDiagramType(data.dt),
    participants: topSequence.participants,
    messages: topSequence.messages,
    fragments: topSequence.fragments,
    participantGroups: topSequence.participantGroups,
    seqLifelineLength: topSequence.seqLifelineLength,
    catalog: decodeCatalog(data.ca, codec),
  };
}

function serializeSocket(socket: SocketDefinition): SerializedSocket {
  return {
    id: socket.id,
    type: socket.type,
    label: socket.label,
    pos: [quantize(socket.position.x), quantize(socket.position.y)],
    tickSide: socket.tickSide,
  };
}

/**
 * Whether this node is exactly what its type would rebuild, box and sockets
 * alike. When it is, none of that geometry needs saving; when it is not, all of
 * it does.
 */
function matchesTypeDefaults(
  node: Node,
  extraInputs: { name: string }[],
  extraOutputs: { name: string }[],
): boolean {
  const rebuilt = buildSocketsFromType(node.type, NODE_WIDTH, extraInputs, extraOutputs);
  const height = calcNodeHeight(
    rebuilt.filter((socket) => socket.type === 'input').length,
    rebuilt.filter((socket) => socket.type === 'output').length,
  );
  if (Math.abs(node.size.width - NODE_WIDTH) > 0.5 || Math.abs(node.size.height - height) > 0.5) return false;
  if (node.sockets.length !== rebuilt.length) return false;
  return node.sockets.every((socket, index) => {
    const other = rebuilt[index];
    return socket.id === other.id
      && socket.type === other.type
      && !socket.tickSide
      && Math.abs(socket.position.x - other.position.x) < 0.5
      && Math.abs(socket.position.y - other.position.y) < 0.5;
  });
}

function serializeNodes(nodes: Node[], edges: Edge[]): { nodes: SerializedNode[]; links: SerializedLink[] } {
  const getTypeDef = (type: string) =>
    (NODE_TYPES as Record<string, { inputs: readonly string[]; outputs: readonly string[] } | undefined>)[type];

  const serializedNodes: SerializedNode[] = nodes.map((node) => {
    const isBoundary = BOUNDARY_TYPES.has(node.type);
    const isGroupNode = node.type === 'group' || !!node.childGraphId;
    const def = getTypeDef(node.type);
    const baseIn = def?.inputs.length ?? 0;
    const baseOut = def?.outputs.length ?? 0;
    const inputs = node.sockets.filter((socket) => socket.type === 'input');
    const outputs = node.sockets.filter((socket) => socket.type === 'output');

    const result: SerializedNode = {
      id: node.id,
      type: node.type,
      name: node.label,
      pos: [quantize(node.position.x), quantize(node.position.y)],
    };

    if (isBoundary || isGroupNode) {
      result.size = [quantize(node.size.width), quantize(node.size.height)];
      result.sockets = node.sockets.map(serializeSocket);
      if (node.childGraphId) result.childGraphId = node.childGraphId;
    } else {
      if (isMarkupType(node.type) || GENERAL_SHAPE_TYPES.has(node.type) || isMagneticNode(node.type)) {
        result.size = [quantize(node.size.width), quantize(node.size.height)];
      }
      const extraInputs = inputs.slice(baseIn).map((socket) => ({ name: socket.label ?? '' }));
      const extraOutputs = outputs.slice(baseOut).map((socket) => ({ name: socket.label ?? '' }));
      if (extraInputs.length > 0) result.extra_inputs = extraInputs;
      if (extraOutputs.length > 0) result.extra_outputs = extraOutputs;
      // A node whose box and sockets are exactly what its type produces stays
      // as short as it ever was, which is what keeps a file hand-writable. Once
      // layout or an import has sized the box to a label and spread the sockets
      // one per edge, none of that can be inferred from the type any more, so it
      // is written down - otherwise reopening the file rebuilds a different
      // diagram from the one that was saved.
      if (!isMarkupType(node.type) && !matchesTypeDefaults(node, extraInputs, extraOutputs)) {
        result.size = [quantize(node.size.width), quantize(node.size.height)];
        result.sockets = node.sockets.map(serializeSocket);
      }
    }

    if (node.layerId && node.layerId !== 'default') result.layer = node.layerId;
    if (node.parentGroupId) result.parentGroupId = node.parentGroupId;
    if (node.type === 'diamond') result.diamondRotation = node.data?.diamondRotation ?? 0;
    if (node.subPageId) result.subPageId = node.subPageId;
    if (node.linkedPageId) result.linkedPageId = node.linkedPageId;
    if (node.catalogId) result.catalogId = node.catalogId;
    if (typeof node.data?.stackParentId === 'string') result.stackParentId = node.data.stackParentId;
    if (node.data?.stackSnap === 'center' || node.data?.stackSnap === 'x' || node.data?.stackSnap === 'y') {
      result.stackSnap = node.data.stackSnap;
    }
    const attributes = Array.isArray(node.data?.attributes) ? node.data.attributes as string[] : undefined;
    const methods = Array.isArray(node.data?.methods) ? node.data.methods as string[] : undefined;
    const stereotype = typeof node.data?.stereotype === 'string' ? node.data.stereotype : undefined;
    if (attributes?.length || methods?.length || stereotype) {
      result.uml = { attributes, methods, stereotype };
    }
    if (isMarkupType(node.type)) {
      const from = node.data?.from;
      const to = node.data?.to;
      result.markup = {
        from: from ? [quantize(from.x), quantize(from.y)] : undefined,
        to: to ? [quantize(to.x), quantize(to.y)] : undefined,
        edgeType: node.data?.edgeType,
        strokeWidth: node.data?.strokeWidth,
        dashStyle: node.data?.dashStyle,
        color: typeof node.data?.color === 'string' ? node.data.color : undefined,
      };
    }

    if (node.meta) {
      const meta: SerializedNode['meta'] = {};
      if (node.meta.description) meta.description = node.meta.description;
      if (node.meta.owner) meta.owner = node.meta.owner;
      if (node.meta.status) meta.status = node.meta.status;
      if (node.meta.links && node.meta.links.length > 0) meta.links = node.meta.links;
      if (Object.keys(meta).length > 0) result.meta = meta;
    }

    return result;
  });

  const serializedLinks: SerializedLink[] = edges.map((edge) => {
    const sourceNode = nodes.find((node) => node.id === edge.sourceNodeId);
    const targetNode = nodes.find((node) => node.id === edge.targetNodeId);
    const sourceIsBoundary = BOUNDARY_TYPES.has(sourceNode?.type ?? '');
    const targetIsBoundary = BOUNDARY_TYPES.has(targetNode?.type ?? '');
    const sourceIsGroup = sourceNode?.type === 'group' || !!sourceNode?.childGraphId;
    const targetIsGroup = targetNode?.type === 'group' || !!targetNode?.childGraphId;
    const sourceSocket = sourceNode?.sockets.find((socket) => socket.id === edge.sourceSocketId);
    const targetSocket = targetNode?.sockets.find((socket) => socket.id === edge.targetSocketId);
    const sourceUseString = sourceIsBoundary || sourceIsGroup || isMagneticNode(sourceNode?.type ?? '');
    const targetUseString = targetIsBoundary || targetIsGroup || isMagneticNode(targetNode?.type ?? '');

    const out: SerializedLink = {
      from: edge.sourceNodeId,
      out: sourceUseString
        ? (sourceSocket?.id ?? edge.sourceSocketId)
        : (sourceSocket ? parseInt(sourceSocket.id.replace('out-', ''), 10) : 0),
      to: edge.targetNodeId,
      in: targetUseString
        ? (targetSocket?.id ?? edge.targetSocketId)
        : (targetSocket ? parseInt(targetSocket.id.replace('in-', ''), 10) : 0),
    };

    if (edge.label) out.label = edge.label;
    if (edge.animated) out.animated = edge.animated;
    if (edge.waypoints && edge.waypoints.length > 0) {
      out.waypoints = edge.waypoints.map((point) => [quantize(point.x), quantize(point.y)]);
    }
    if (edge.autoRoute) {
      out.autoRoute = {
        from: [quantize(edge.autoRoute.from.x), quantize(edge.autoRoute.from.y)],
        to: [quantize(edge.autoRoute.to.x), quantize(edge.autoRoute.to.y)],
        points: edge.autoRoute.points.map((point) => [quantize(point.x), quantize(point.y)]),
      };
    }
    if (edge.routingMode) out.routing = edge.routingMode;
    if (edge.curveStrength !== undefined && edge.curveStrength !== 1.0) out.curve = quantize(edge.curveStrength);
    if (edge.strokeWidth !== undefined) out.strokeWidth = edge.strokeWidth;
    if (edge.dashStyle) out.dashStyle = edge.dashStyle;
    if (edge.edgeType) out.edgeType = edge.edgeType;
    if (edge.color) out.color = edge.color;

    return out;
  });

  return { nodes: serializedNodes, links: serializedLinks };
}

function buildV3Data(state: {
  diagramType?: DiagramType;
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; scale: number };
  layers: Layer[];
  groups?: NodeGroup[];
  graphs?: Record<string, Graph>;
  activeGraphId?: string;
  pages?: DiagramPage[];
  activePage?: string;
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  seqLifelineLength?: number;
  catalog?: CatalogComponent[];
}): DiagramData {
  const { nodes: serializedNodes, links: serializedLinks } = serializeNodes(state.nodes, state.edges);
  const activePageId = state.activePage ?? state.pages?.[0]?.id;

  const serializedGraphs: Record<string, SerializedGraph> = {};
  if (state.graphs) {
    for (const [graphId, graph] of Object.entries(state.graphs)) {
      const serialized = serializeNodes(graph.nodes, graph.edges);
      serializedGraphs[graphId] = {
        id: graph.id,
        nodes: serialized.nodes,
        links: serialized.links,
        viewport: {
          x: quantize(graph.viewport.x),
          y: quantize(graph.viewport.y),
          scale: quantize(graph.viewport.scale),
        },
      };
    }
  }

  const syncedPages: DiagramPage[] = (state.pages ?? []).map((page) => (
    page.id === activePageId
      ? {
          ...page,
          diagramType: state.diagramType ?? page.diagramType ?? 'flow',
          kit: page.kit,
          nodes: state.nodes,
          edges: state.edges,
          groups: state.groups ?? [],
          layers: state.layers,
          viewport: state.viewport,
          participants: state.participants ?? page.participants ?? [],
          messages: state.messages ?? page.messages ?? [],
          fragments: state.fragments ?? page.fragments ?? [],
          participantGroups: state.participantGroups ?? page.participantGroups ?? [],
          seqLifelineLength: state.seqLifelineLength ?? page.seqLifelineLength,
          settings: page.settings,
        }
      : page
  ));

  const serializedPages: SerializedPage[] = syncedPages.map((page) => {
    const serialized = serializeNodes(page.nodes, page.edges);
    return {
      id: page.id,
      name: page.name,
      diagramType: page.diagramType,
      kit: page.kit,
      nodes: serialized.nodes,
      links: serialized.links,
      viewport: {
        x: quantize(page.viewport.x),
        y: quantize(page.viewport.y),
        scale: quantize(page.viewport.scale),
      },
      layers: page.layers,
      groups: page.groups,
      participants: page.participants ?? [],
      messages: page.messages ?? [],
      fragments: page.fragments ?? [],
      participantGroups: page.participantGroups ?? [],
      seqLifelineLength: page.seqLifelineLength,
      settings: page.settings,
    };
  });

  const hasGraphs = Object.keys(serializedGraphs).length > 0;
  const hasPages = serializedPages.length > 0;

  return {
    version: '3.0',
    name: 'My Architecture',
    graphs: hasGraphs ? serializedGraphs : undefined,
    activeGraphId: state.activeGraphId,
    pages: hasPages ? serializedPages : undefined,
    activePage: hasPages ? (state.activePage ?? 'main') : undefined,
    nodes: !hasGraphs && !hasPages ? serializedNodes : undefined,
    links: !hasGraphs && !hasPages ? serializedLinks : undefined,
    viewport: !hasGraphs && !hasPages
      ? { x: quantize(state.viewport.x), y: quantize(state.viewport.y), scale: quantize(state.viewport.scale) }
      : undefined,
    layers: !hasGraphs && !hasPages ? state.layers : undefined,
    groups: !hasGraphs && !hasPages ? (state.groups ?? []) : undefined,
    diagramType: state.diagramType,
    participants: state.participants ?? [],
    messages: state.messages ?? [],
    fragments: state.fragments ?? [],
    participantGroups: state.participantGroups ?? [],
    seqLifelineLength: state.seqLifelineLength,
    catalog: state.catalog,
  };
}

function deserializeNodes(rawNodes: SerializedNode[], rawLinks: SerializedLink[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = rawNodes.map((rawNode) => {
    const isBoundary = BOUNDARY_TYPES_DESER.has(rawNode.type);
    const isGroupNode = rawNode.type === 'group' || !!rawNode.childGraphId;
    const extraInputs = rawNode.extra_inputs ?? [];
    const extraOutputs = rawNode.extra_outputs ?? [];
    const isDecision = rawNode.type === 'diamond';
    const decisionRotation = rawNode.diamondRotation ?? 0;

    let sockets: SocketDefinition[];
    let nodeSize;
    // Written-down sockets win over rebuilt ones for every node that has them.
    // A file that omits them still gets the type's own layout, which is what
    // lets one be written by hand.
    if (rawNode.sockets && !isMarkupType(rawNode.type)) {
      sockets = rawNode.sockets.map((socket) => ({
        id: socket.id,
        type: socket.type,
        dataType: 'data' as const,
        label: socket.label,
        position: { x: socket.pos[0], y: socket.pos[1] },
        tickSide: socket.tickSide,
      }));
      nodeSize = rawNode.size
        ? { width: rawNode.size[0], height: rawNode.size[1] }
        : (isBoundary || isGroupNode) ? { width: 220, height: 80 } : { width: NODE_WIDTH, height: calcNodeHeight(
          sockets.filter((socket) => socket.type === 'input').length,
          sockets.filter((socket) => socket.type === 'output').length,
        ) };
    } else if (isMarkupType(rawNode.type)) {
      const blueprint = NodeFactory.create(rawNode.type, 0);
      sockets = [];
      nodeSize = rawNode.size
        ? { width: rawNode.size[0], height: rawNode.size[1] }
        : blueprint.size;
    } else if (isMagneticNode(rawNode.type) || isDecision) {
      const fallback = isDecision ? calcDecisionSize(decisionRotation) : { width: 160, height: 90 };
      const w = rawNode.size?.[0] ?? fallback.width;
      const h = rawNode.size?.[1] ?? fallback.height;
      sockets = rebuildSketchSockets(rawNode.type, w, h, 0);
      nodeSize = { width: w, height: h };
    } else if (GENERAL_SHAPE_TYPES.has(rawNode.type) && rawNode.size) {
      sockets = buildSocketsFromType(rawNode.type, rawNode.size[0], extraInputs, extraOutputs);
      nodeSize = { width: rawNode.size[0], height: rawNode.size[1] };
    } else {
      sockets = buildSocketsFromType(rawNode.type, NODE_WIDTH, extraInputs, extraOutputs);
      const totalInputs = sockets.filter((socket) => socket.type === 'input').length;
      const totalOutputs = sockets.filter((socket) => socket.type === 'output').length;
      nodeSize = { width: NODE_WIDTH, height: calcNodeHeight(totalInputs, totalOutputs) };
    }

    const meta: NodeMeta | undefined = rawNode.meta ? {
      description: rawNode.meta.description,
      owner: rawNode.meta.owner,
      status: rawNode.meta.status as NodeMeta['status'],
      links: rawNode.meta.links,
    } : undefined;

    return {
      id: rawNode.id,
      type: rawNode.type,
      position: { x: rawNode.pos[0], y: rawNode.pos[1] },
      size: nodeSize,
      label: rawNode.name,
      sockets,
      data: {
        color: rawNode.markup?.color
          ?? (NODE_TYPES as Record<string, { color: string } | undefined>)[rawNode.type]?.color
          ?? (isGroupNode ? '#64748b' : '#6366f1'),
        ...(isDecision ? { diamondRotation: decisionRotation } : {}),
        ...(rawNode.markup?.from ? { from: { x: rawNode.markup.from[0], y: rawNode.markup.from[1] } } : {}),
        ...(rawNode.markup?.to ? { to: { x: rawNode.markup.to[0], y: rawNode.markup.to[1] } } : {}),
        ...(rawNode.markup?.edgeType ? { edgeType: rawNode.markup.edgeType } : {}),
        ...(rawNode.markup?.strokeWidth !== undefined ? { strokeWidth: rawNode.markup.strokeWidth } : {}),
        ...(rawNode.markup?.dashStyle ? { dashStyle: rawNode.markup.dashStyle } : {}),
        ...(rawNode.uml?.attributes ? { attributes: rawNode.uml.attributes } : {}),
        ...(rawNode.uml?.methods ? { methods: rawNode.uml.methods } : {}),
        ...(rawNode.uml?.stereotype ? { stereotype: rawNode.uml.stereotype } : {}),
        ...(rawNode.stackParentId ? { stackParentId: rawNode.stackParentId } : {}),
        ...(rawNode.stackSnap ? { stackSnap: rawNode.stackSnap } : {}),
      },
      layerId: rawNode.layer,
      parentGroupId: rawNode.parentGroupId,
      childGraphId: rawNode.childGraphId,
      meta,
      subPageId: rawNode.subPageId,
      linkedPageId: rawNode.linkedPageId,
      catalogId: rawNode.catalogId,
    };
  });

  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const now = Date.now();

  const edges: Edge[] = rawLinks.map((link, index) => {
    const sourceNode = nodeMap.get(link.from);
    const targetNode = nodeMap.get(link.to);
    const sourceIsBoundary = BOUNDARY_TYPES_DESER.has(sourceNode?.type ?? '');
    const targetIsBoundary = BOUNDARY_TYPES_DESER.has(targetNode?.type ?? '');
    const sourceIsGroup = sourceNode?.type === 'group' || !!sourceNode?.childGraphId;
    const targetIsGroup = targetNode?.type === 'group' || !!targetNode?.childGraphId;
    const sourceUseString = sourceIsBoundary || sourceIsGroup || isMagneticNode(sourceNode?.type ?? '');
    const targetUseString = targetIsBoundary || targetIsGroup || isMagneticNode(targetNode?.type ?? '');

    return {
      id: `edge-loaded-${index}-${now}`,
      sourceNodeId: link.from,
      sourceSocketId: sourceUseString ? String(link.out) : `out-${link.out}`,
      targetNodeId: link.to,
      targetSocketId: targetUseString ? String(link.in) : `in-${link.in}`,
      label: link.label,
      animated: link.animated,
      waypoints: link.waypoints?.map(([x, y]) => ({ x, y })),
      autoRoute: link.autoRoute
        ? {
          from: { x: link.autoRoute.from[0], y: link.autoRoute.from[1] },
          to: { x: link.autoRoute.to[0], y: link.autoRoute.to[1] },
          points: link.autoRoute.points.map(([x, y]) => ({ x, y })),
        }
        : undefined,
      routingMode: link.routing as Edge['routingMode'],
      curveStrength: link.curve,
      strokeWidth: link.strokeWidth,
      dashStyle: link.dashStyle,
      edgeType: link.edgeType,
      color: link.color,
    };
  });

  return { nodes, edges };
}

export function saveToJSON(state: {
  diagramType?: DiagramType;
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; scale: number };
  layers: Layer[];
  groups?: NodeGroup[];
  graphs?: Record<string, Graph>;
  activeGraphId?: string;
  pages?: DiagramPage[];
  activePage?: string;
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  seqLifelineLength?: number;
  catalog?: CatalogComponent[];
}): string {
  const v3Data = buildV3Data(state);
  const v4Data = encodeV4(v3Data);
  return JSON.stringify(v4Data);
}

function attachCatalog(
  loaded: {
    diagramType: DiagramType;
    nodes: Node[];
    edges: Edge[];
    viewport: { x: number; y: number; scale: number };
    layers: Layer[];
    groups: NodeGroup[];
    graphs: Record<string, Graph>;
    activeGraphId: string;
    pages: DiagramPage[];
    activePage: string;
    participants: SequenceParticipant[];
    messages: SequenceMessage[];
    fragments: SequenceFragment[];
    participantGroups: SequenceParticipantGroup[];
    seqLifelineLength: number;
  },
  catalog?: CatalogComponent[],
) {
  const resolved = catalog && catalog.length > 0
    ? catalog
    : inferCatalogFromDocument({
      catalog: [],
      nodes: loaded.nodes,
      participants: loaded.participants,
      pages: loaded.pages,
      graphs: loaded.graphs,
    });
  return { ...loaded, catalog: resolved };
}

export function loadFromJSON(text: string): {
  diagramType: DiagramType;
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; scale: number };
  layers: Layer[];
  groups: NodeGroup[];
  graphs: Record<string, Graph>;
  activeGraphId: string;
  pages: DiagramPage[];
  activePage: string;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
  catalog: CatalogComponent[];
} {
  let parsed = JSON.parse(text);

  if (Array.isArray(parsed)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw JSON parse
    const rawNodes: Node[] = parsed.map((node: Record<string, any>) => ({
      id: node.id,
      type: node.type,
      label: node.label ?? node.name ?? '',
      position: node.position ?? { x: 0, y: 0 },
      size: node.size ?? { width: 180, height: 84 },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw JSON parse
      sockets: (node.sockets ?? []).map((socket: Record<string, any>) => ({
        id: socket.id,
        type: socket.type,
        dataType: socket.dataType ?? 'data',
        position: socket.position ?? { x: 0, y: 0 },
        label: socket.label ?? '',
        order: socket.order ?? 0,
      })),
      data: node.data,
      meta: node.meta,
      layerId: node.layerId,
    }));

    const viewport = { x: 0, y: 0, scale: 1 };
    const mainPage: DiagramPage = {
      id: 'main',
      name: 'Main',
      diagramType: 'flow',
      nodes: rawNodes,
      edges: [],
      layers: [{ ...DEFAULT_LAYER }],
      groups: [],
      viewport,
      participants: [],
      messages: [],
      fragments: [],
      participantGroups: [],
      seqLifelineLength: 500,
    };

    return attachCatalog({
      diagramType: 'flow',
      nodes: rawNodes,
      edges: [],
      viewport,
      layers: [{ ...DEFAULT_LAYER }],
      groups: [],
      graphs: { root: { id: 'root', nodes: rawNodes, edges: [], viewport } },
      activeGraphId: 'root',
      pages: [mainPage],
      activePage: 'main',
      participants: [],
      messages: [],
      fragments: [],
      participantGroups: [],
      seqLifelineLength: 500,
    });
  }

  if (parsed && typeof parsed === 'object' && typeof parsed.version === 'string' && parsed.version.startsWith('4')) {
    parsed = decodeV4(parsed as DiagramDataV4);
  }

  const data: DiagramData = parsed;

  let graphs: Record<string, Graph> = {};
  if (data.graphs && Object.keys(data.graphs).length > 0) {
    for (const [graphId, serializedGraph] of Object.entries(data.graphs)) {
      const graph = deserializeNodes(serializedGraph.nodes ?? [], serializedGraph.links ?? []);
      graphs[graphId] = {
        id: serializedGraph.id,
        nodes: graph.nodes,
        edges: graph.edges,
        viewport: serializedGraph.viewport ?? { x: 0, y: 0, scale: 1 },
      };
    }
  }

  const activeGraphId = data.activeGraphId ?? 'root';

  if (data.pages && data.pages.length > 0) {
    const pages: DiagramPage[] = data.pages.map((page) => {
      const graph = deserializeNodes(page.nodes ?? [], page.links ?? []);
      const layers: Layer[] = (page.layers && page.layers.length > 0) ? page.layers : [{ ...DEFAULT_LAYER }];
      const groups: NodeGroup[] = (page.groups ?? []).map((group) => ({ ...group, childGroupIds: group.childGroupIds ?? [] }));
      return {
        id: page.id,
        name: page.name,
        diagramType: page.diagramType ?? data.diagramType ?? 'flow',
        kit: isDiagramKitId(page.kit) ? page.kit : undefined,
        nodes: graph.nodes,
        edges: graph.edges,
        layers,
        groups,
        viewport: page.viewport ?? { x: 0, y: 0, scale: 1 },
        participants: page.participants ?? [],
        messages: page.messages ?? [],
        fragments: page.fragments ?? [],
        participantGroups: page.participantGroups ?? [],
        seqLifelineLength: page.seqLifelineLength ?? 500,
        settings: page.settings,
      };
    });

    const activePageId = data.activePage ?? pages[0].id;
    const activePage = pages.find((page) => page.id === activePageId) ?? pages[0];
    const activeDiagramType = activePage.diagramType ?? data.diagramType ?? 'flow';

    if (Object.keys(graphs).length === 0) {
      graphs = {
        root: {
          id: 'root',
          nodes: activePage.nodes,
          edges: activePage.edges,
          viewport: activePage.viewport,
        },
      };
    }

    const activeGraph = graphs[activeGraphId] ?? graphs.root;

    return attachCatalog({
      diagramType: activeDiagramType,
      nodes: activeGraph?.nodes ?? activePage.nodes,
      edges: activeGraph?.edges ?? activePage.edges,
      viewport: activeGraph?.viewport ?? activePage.viewport,
      layers: activePage.layers,
      groups: activePage.groups,
      graphs,
      activeGraphId,
      pages,
      activePage: activePageId,
      participants: activePage.participants ?? data.participants ?? [],
      messages: activePage.messages ?? data.messages ?? [],
      fragments: activePage.fragments ?? data.fragments ?? [],
      participantGroups: activePage.participantGroups ?? data.participantGroups ?? [],
      seqLifelineLength: activePage.seqLifelineLength ?? 500,
    }, data.catalog);
  }

  const graph = deserializeNodes(data.nodes ?? [], data.links ?? []);
  const layers: Layer[] = (data.layers && data.layers.length > 0) ? data.layers : [{ ...DEFAULT_LAYER }];
  const groups: NodeGroup[] = (data.groups ?? []).map((group) => ({ ...group, childGroupIds: group.childGroupIds ?? [] }));
  const viewport = data.viewport ?? { x: 0, y: 0, scale: 1 };

  const mainPage: DiagramPage = {
    id: 'main',
    name: 'Main',
    diagramType: data.diagramType ?? 'flow',
    nodes: graph.nodes,
    edges: graph.edges,
    layers,
    groups,
    viewport,
    participants: data.participants ?? [],
    messages: data.messages ?? [],
    fragments: data.fragments ?? [],
    participantGroups: data.participantGroups ?? [],
    seqLifelineLength: data.seqLifelineLength ?? 500,
  };

  if (Object.keys(graphs).length === 0) {
    graphs = {
      root: { id: 'root', nodes: graph.nodes, edges: graph.edges, viewport },
    };
  }

  return attachCatalog({
    diagramType: data.diagramType ?? 'flow',
    nodes: graph.nodes,
    edges: graph.edges,
    viewport,
    layers,
    groups,
    graphs,
    activeGraphId: 'root',
    pages: [mainPage],
    activePage: 'main',
    participants: data.participants ?? [],
    messages: data.messages ?? [],
    fragments: data.fragments ?? [],
    participantGroups: data.participantGroups ?? [],
    seqLifelineLength: data.seqLifelineLength ?? 500,
  }, data.catalog);
}
